import {
  useEffect, useContext,
} from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from '@gatsbyjs/reach-router';
import { ErrorManagement } from '@printdeal/error-management/react';
import { decodePrintdealToken } from '../helpers/auth';
import { selectors as authSelectors } from '../store/slices/authSlice';
import AnalyticsTrackerContext from '../components/AnalyticsTracker/AnalyticsTrackerContext';
import { findListIdforRecommendedProduct } from '../components/AnalyticsTracker/AnalyticsTrackerHelper';
import SegmentContext, { ISegmentContext } from '../components/segmentation/SegmentContext';

export const EVENT_TYPE = {
  PRODUCT_VIEWED: 'Product Viewed',
  PRODUCT_LIST_VIEWED: 'Product List Viewed',
  PRODUCT_ADDED: 'Product Added',
  PRODUCT_REMOVED: 'Product Removed',
  CART_VIEWED: 'Cart Viewed',
  CHECKOUT_STARTED: 'Checkout Started',
  CHECKOUT_STEP_VIEWED: 'Checkout Step Viewed',
  CHECKOUT_STEP_COMPLETED: 'Checkout Step Completed',
  ORDER_CREATED: 'Order Completed',
};

type SegmentEventType = typeof EVENT_TYPE[keyof typeof EVENT_TYPE];

interface ISegmentTrackEvent {
  event: string;
  userId: string | undefined;
  store: string;
  label: string;
}

interface ISegmentPDPProductViewData {
  label: string;
  PrUUID: string;
  product_id: string;
  sku: string;
  masterVariant: string;
  masterVariantId: string;
  name: string;
  category?: string;
  brand?: string;
  variant?: string;
  price?: number;
  quantity?: number;
  coupon?: string;
  currency: string;
  position?: number;
  value?: number;
  image_url?: string;
}

interface ISegmentProductViewTrackEvent extends ISegmentPDPProductViewData, ISegmentTrackEvent {
  url: string;
}

const useSegmentTracking = (event: SegmentEventType, eventData: ISegmentPDPProductViewData) => {
  const isAuthLoading = useSelector(authSelectors.isAuthLoading);
  const { isSegmentAnalyticsReady, consentData, isConsentReady }: ISegmentContext = useContext(SegmentContext);
  const token = useSelector(authSelectors.getAuthToken);
  const location = useLocation();
  const [analyticsTrackerData] = useContext(AnalyticsTrackerContext);
  useEffect(() => {
    if (!isAuthLoading && isSegmentAnalyticsReady && isConsentReady) {
      let userId;
      try {
        userId = decodePrintdealToken(token).account?.id;
      } catch (error) {
        ErrorManagement.exception({
          exception: error,
          tags: {
            component: 'useSegmentTracking',
            fileLocation: 'hooks-useSegmentTracking',
            featureFunction: 'useSegmentTracking-decodePrintdealToken-prevent-crash',
          },
          fingerprint: ['useSegmentTracking-decodePrintdealToken-prevent-crash'],
        });
        return;
      }

      const pageUrl = `${location.origin}${location.pathname}`;
      const store: string = process.env.GATSBY_STORE || 'drukwerkdeal';
      /**
       * Check whether a product viewed, is available in the AnalyticsTracker Context for any CT Placements
       * @returns empty [] or list_id/analyticsId needed for the CT recommendation engine
       */
      const productInAnalyticsTracker = findListIdforRecommendedProduct(
        analyticsTrackerData.ctRecommendations,
        eventData.product_id,
        'productsClicked',
      );
      const segmentData: ISegmentProductViewTrackEvent = {
        userId,
        event,
        store,
        url: pageUrl,
        ...eventData,
        ...(productInAnalyticsTracker.length > 0
          && { list_id: productInAnalyticsTracker[0]?.trackingIds?.analyticsId }),
      };

      window.analytics.track(event, segmentData, {
        context: {
          consentData,
        },
      });
    }
  }, [isAuthLoading, isSegmentAnalyticsReady, isConsentReady]);
};

export default useSegmentTracking;
